/**
 * InvoiceList Component - Responsive container for invoice display
 * Shows cards on mobile/tablet, table on desktop
 */

import React from "react";
import { Box, Typography } from "@mui/material";
import InvoiceCard from "./InvoiceCard.jsx";
import InvoiceTableDesktop from "./InvoiceTableDesktop.jsx";
import { useDeviceType } from "../../leads/hooks/useDeviceType.js";

/**
 * InvoiceList Component
 * @param {Object} props
 * @param {Array} props.invoices - Array of invoice objects
 * @param {boolean} props.loading - Loading state
 * @param {Set|Array} props.downloadingPdfIds - Set of invoice IDs currently downloading PDF
 * @param {Function} props.onViewInvoice - Callback when viewing invoice
 * @param {Function} props.onDownloadPdf - Callback when downloading PDF
 * @param {Function} props.onSendInvoice - Callback when sending invoice
 * @param {Function} props.onRemindInvoice - Callback when reminding about invoice
 * @param {boolean} props.showActions - Whether to show actions (deprecated, always true)
 * @param {Function} props.onFollowUpNavigate - Callback when navigating to follow-up
 */
const InvoiceList = ({
  invoices,
  loading,
  downloadingPdfIds = new Set(),
  onViewInvoice,
  onDownloadPdf,
  onSendInvoice,
  onRemindInvoice,
  showActions = true,
  onFollowUpNavigate,
}) => {
  const { isMobile, isTablet, isDesktop } = useDeviceType();

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading invoices...</Typography>
      </Box>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>No invoices found</Typography>
      </Box>
    );
  }

  // Convert Set to array for easier checking
  const downloadingIdsSet = downloadingPdfIds instanceof Set ? downloadingPdfIds : new Set(downloadingPdfIds || []);

  // Mobile and Tablet: Card view
  if (isMobile || isTablet) {
    return (
      <Box>
        {invoices.map((invoice) => (
          <InvoiceCard
            key={invoice.id || invoice._id}
            invoice={invoice}
            isDownloadingPdf={downloadingIdsSet.has(invoice.id || invoice._id)}
            onViewInvoice={onViewInvoice}
            onDownloadPdf={onDownloadPdf}
            onSendInvoice={onSendInvoice}
            onRemindInvoice={onRemindInvoice}
            onFollowUpNavigate={onFollowUpNavigate}
          />
        ))}
      </Box>
    );
  }

  // Desktop: Table view
  return (
    <InvoiceTableDesktop
      invoices={invoices}
      downloadingPdfIds={downloadingIdsSet}
      onViewInvoice={onViewInvoice}
      onDownloadPdf={onDownloadPdf}
      onSendInvoice={onSendInvoice}
      onRemindInvoice={onRemindInvoice}
      onFollowUpNavigate={onFollowUpNavigate}
    />
  );
};

export default InvoiceList;

